import React, { useEffect, useState } from 'react';
import { Alert, Button, PageHeader, Result, Skeleton, Table, Tag, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { ancanaApi } from '../../api/ancanaApi';

/**
 * What each staff role may do, per module — a read-only view of
 * `ancana_api/tenancy/roles.py` (served at `GET /v1/tenancy/roles/`).
 *
 * Read-only on purpose: the table is code, so changing a role is a reviewed
 * edit to that file, not a click here. This page is for answering "can ops
 * issue invoices?" without opening the repo.
 */

type Level = 'none' | 'read' | 'write';

interface IModule { key: string; label: string; members: string[] }
interface IRole { key: string; label: string; platform: boolean; access: Record<string, Level> | null }
interface IRoleMatrix { modules: IModule[]; roles: IRole[] }

const LEVELS: Record<Level, { color: string; label: string }> = {
  write: { color: 'green', label: 'Read & write' },
  read: { color: 'blue', label: 'Read only' },
  none: { color: 'default', label: 'No access' },
};

const Container = styled.div`
  height: 100%;
  overflow-y: auto;

  .body {
    padding: 0 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .members {
    color: var(--ancana-gray);
    font-size: 12px;
  }
`;

function Roles() {
  const [matrix, setMatrix] = useState<IRoleMatrix>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    setError(undefined);
    ancanaApi.get('tenancy/roles/')
      .then(res => setMatrix(res.data))
      .catch(err => setError(err.response?.status === 403
        ? 'Only platform staff can see the role matrix.'
        : 'Could not load the role matrix.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const columns = [
    {
      title: 'Module',
      key: 'module',
      fixed: 'left' as const,
      width: 220,
      render: (m: IModule) => (
        <>
          <div>{m.label}</div>
          <div className="members">{m.members.join(', ')}</div>
        </>
      ),
    },
    ...(matrix?.roles ?? []).map(role => ({
      title: (
        <Tooltip title={role.platform ? 'Platform role: applies to every tenant' : 'Applies within the tenant the membership is on'}>
          {role.label}{role.platform ? <Tag style={{ marginLeft: 6 }}>platform</Tag> : null}
        </Tooltip>
      ),
      key: role.key,
      align: 'center' as const,
      render: (m: IModule) => {
        if (!role.access) {
          return <Tooltip title="Not granted by role — this role keeps working on Django model permissions."><Tag>Model permissions</Tag></Tooltip>;
        }
        const level = LEVELS[role.access[m.key] ?? 'none'];
        return <Tag color={level.color}>{level.label}</Tag>;
      },
    })),
  ];

  return (
    <Container>
      <PageHeader
        title="Roles"
        subTitle="What each staff role can do, per module"
        extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Reload</Button>}
      />
      <div className="body">
        <Alert
          type="info"
          showIcon
          message="Roles add access on top of Django model permissions, and only to a tenant's own data."
          description={<>
            Shared data (users, locations, blog, SEO) stays on model permissions. To change this table,
            edit <code>ancana_api/tenancy/roles.py</code>.
          </>}
        />
        {error ? (
          <Result status="warning" title={error} />
        ) : loading && !matrix ? (
          <Skeleton active />
        ) : (
          <Table
            rowKey="key"
            dataSource={matrix?.modules ?? []}
            columns={columns}
            pagination={false}
            scroll={{ x: true }}
            size="middle"
          />
        )}
      </div>
    </Container>
  );
}

export default Roles;
